import React, { useRef, useState } from 'react';
import { Camera, ImageUp, Loader2, X } from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';
import CameraCaptureModal from './CameraCaptureModal';
import { downscaleImage } from '../lib/downscaleImage';

/**
 * PhotoDocPicker — reusable single-image slot for driver photo + documents.
 * Supports two upload modes:
 *   1) In-app camera capture via `CameraCaptureModal` (getUserMedia).
 *   2) Native file picker (mobile: uses rear camera via `capture="environment"`).
 *
 * Images are downscaled client-side to <5MB before hitting /api/files so
 * drivers on flaky mobile networks aren't dropped.
 *
 * Props:
 *   label          — top label of the slot (e.g. "Profile photo")
 *   value          — current URL string (empty for none)
 *   onChange(url)  — called with the newly uploaded URL after upload succeeds
 *   testIdPrefix   — used for data-testid attributes ({prefix}-camera-btn, ...)
 *   cameraTitle    — modal title when in-app camera is opened
 *   compact        — smaller aspect ratio (used for docs vs. profile avatar)
 */
const PhotoDocPicker = ({ label, value, onChange, testIdPrefix = 'photo', cameraTitle, compact = false }) => {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);

  const uploadFile = async (f) => {
    if (!f) return;
    if (!f.type?.startsWith('image/')) { toast.error('Please pick an image'); return; }
    setBusy(true);
    try {
      let toUpload = f;
      try {
        toUpload = await downscaleImage(f, { maxBytes: 5 * 1024 * 1024 });
      } catch (_e) { /* best-effort */ }
      const fd = new FormData();
      fd.append('file', toUpload);
      const { data: uploaded } = await api.post('/files', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const relOrAbs = uploaded.url || `/api/files/${uploaded.id || uploaded.file_id}`;
      // Prefix with backend origin if the API returned a relative /api/files URL.
      const fullUrl = relOrAbs.startsWith('http')
        ? relOrAbs
        : `${process.env.REACT_APP_BACKEND_URL || ''}${relOrAbs}`;
      onChange?.(fullUrl);
      const before = (f.size / (1024 * 1024)).toFixed(2);
      const after = (toUpload.size / (1024 * 1024)).toFixed(2);
      toast.success(`${label || 'Photo'} saved ${before !== after ? `(auto-resized ${before}MB → ${after}MB)` : ''}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const onFileInput = (e) => uploadFile(e.target.files?.[0]);
  const onCameraCapture = (file) => uploadFile(file);
  const clear = () => onChange?.('');

  const box = compact
    ? 'aspect-[4/3] w-full'
    : 'aspect-square w-full max-w-[220px]';

  return (
    <div className="w-full">
      {label && <div className="text-xs text-slate-600 mb-1.5 font-medium">{label}</div>}
      <div className={`relative ${box} rounded-xl overflow-hidden border-2 border-dashed border-slate-200 bg-slate-50`} data-testid={`${testIdPrefix}-slot`}>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={onFileInput}
          data-testid={`${testIdPrefix}-file-input`}
        />
        {value ? (
          <>
            <img src={value} alt={label || 'Photo'} className="absolute inset-0 w-full h-full object-cover" data-testid={`${testIdPrefix}-preview`} />
            <button
              type="button"
              onClick={clear}
              disabled={busy}
              className="absolute top-1.5 right-1.5 h-7 w-7 rounded-full bg-white/90 text-slate-700 flex items-center justify-center hover:bg-white shadow"
              data-testid={`${testIdPrefix}-clear-btn`}
              aria-label="Remove photo"
              title="Remove photo"
            >
              <X className="h-3.5 w-3.5" />
            </button>
            <div className="absolute bottom-1.5 right-1.5 flex gap-1">
              <button
                type="button"
                onClick={() => setCameraOpen(true)}
                disabled={busy}
                className="h-7 w-7 rounded-full bg-emerald-600 text-white flex items-center justify-center hover:bg-emerald-700 shadow"
                data-testid={`${testIdPrefix}-retake-camera-btn`}
                aria-label="Retake with camera"
                title="Retake with camera"
              >
                <Camera className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={busy}
                className="h-7 w-7 rounded-full bg-white/90 text-slate-700 flex items-center justify-center hover:bg-white shadow"
                data-testid={`${testIdPrefix}-retake-file-btn`}
                aria-label="Choose from files"
                title="Choose from files"
              >
                <ImageUp className="h-3.5 w-3.5" />
              </button>
            </div>
          </>
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 p-2">
            {busy ? (
              <Loader2 className="h-6 w-6 text-emerald-600 animate-spin" data-testid={`${testIdPrefix}-busy`} />
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => setCameraOpen(true)}
                  className="w-full flex-1 flex flex-col items-center justify-center gap-1 rounded-md bg-emerald-600 text-white hover:bg-emerald-700 active:scale-[0.98] transition"
                  data-testid={`${testIdPrefix}-camera-btn`}
                  aria-label="Take with camera"
                >
                  <Camera className="h-5 w-5" />
                  <span className="text-[10px] font-semibold uppercase tracking-wider">Take photo</span>
                </button>
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="w-full text-[10px] uppercase tracking-wider text-slate-500 hover:text-emerald-700 flex items-center justify-center gap-1 py-0.5"
                  data-testid={`${testIdPrefix}-file-btn`}
                >
                  <ImageUp className="h-3 w-3" /> or upload from gallery
                </button>
              </>
            )}
          </div>
        )}
      </div>
      <CameraCaptureModal
        open={cameraOpen}
        onClose={() => setCameraOpen(false)}
        onCapture={onCameraCapture}
        title={cameraTitle || label || 'Take photo'}
      />
    </div>
  );
};

export default PhotoDocPicker;
